import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { getRemoteConfig } from 'firebase/remote-config';
import { apiClient, fetchBaseUrl } from '../../../api/tools/tools';
import { Button } from '../../common/button/Button';
import { Input } from '../../common/input/Input';
import { SignaturePad } from '../../common/signature-pad/SignaturePad';
import './consultant-sign.scss';

const CONTRACT_DATE = new Date().toLocaleDateString('en-ZA', {
  year: 'numeric', month: 'long', day: 'numeric'
});

/* eslint-disable max-len */
const CONTRACT_SECTIONS = [
  {
    heading: '1. Appointment',
    body: 'The Company appoints the Consultant as a non-exclusive brand representative to promote and market Instant Meals products and services via their social media platforms.'
  },
  {
    heading: '2. Obligations',
    body: 'The Consultant agrees to represent the Instant Meals brand honestly and professionally, disclose their affiliation in all promotional content, comply with applicable advertising guidelines, and not make false or misleading claims about products or services.'
  },
  {
    heading: '3. Compensation',
    body: 'The Consultant will receive a commission on verified sales made through their assigned coupon code, at the rate confirmed in their consultant profile.'
  },
  {
    heading: '4. Intellectual Property',
    body: 'Promotional content incorporating Company branding, logos, or trademarks requires prior written approval from the Company.'
  },
  {
    heading: '5. Confidentiality',
    body: 'The Consultant agrees to keep all pricing, commission structures, and business information shared by the Company confidential.'
  },
  {
    heading: '6. Termination',
    body: 'Either party may terminate this Agreement with 14 days written notice. The Company may terminate immediately for breach of these terms.'
  },
  {
    heading: '7. Governing Law',
    body: 'This Agreement is governed by the laws of the Republic of South Africa.'
  }
];
/* eslint-enable max-len */

const STEPS = [
  { label: 'Review Agreement' },
  { label: 'Confirm Details' },
  { label: 'Sign' }
];

export const ConsultantSignPage = () => {
  const { token } = useParams();

  const [consultant, setConsultant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [signedName, setSignedName] = useState('');
  const [signatureImage, setSignatureImage] = useState(null);
  const [sigMode, setSigMode] = useState('draw');
  const [sendCopy, setSendCopy] = useState(true);
  const [bankName, setBankName] = useState('');
  const [bankAccountHolder, setBankAccountHolder] = useState('');
  const [bankAccountNumber, setBankAccountNumber] = useState('');
  const [bankBranchCode, setBankBranchCode] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    fetchBaseUrl(getRemoteConfig())
      .then((base) => apiClient.get(`${base}/consultants/sign/${token}`))
      .then((res) => {
        setConsultant(res.data);
        setLoading(false);
      })
      .catch(() => {
        setLoadError('This signing link is invalid or has already been used.');
        setLoading(false);
      });
  }, [token]);

  const canSubmit = signedName.trim().length > 1 && (sigMode === 'type' || signatureImage);

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    setSubmitError(null);

    try {
      const base = await fetchBaseUrl(getRemoteConfig());
      await apiClient.post(`${base}/consultants/sign/${token}`, {
        signedByName: signedName.trim(),
        signatureImage: sigMode === 'draw' ? signatureImage : null,
        sendCopy,
        bankName: bankName.trim() || null,
        bankAccountHolder: bankAccountHolder.trim() || null,
        bankAccountNumber: bankAccountNumber.trim() || null,
        bankBranchCode: bankBranchCode.trim() || null
      });
      setDone(true);
    } catch {
      setSubmitError('Something went wrong. Please try again or contact info@instant-meals.co.za.');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="sign-page sign-page--centered">
        <div className="sign-page__spinner" />
        <p className="sign-page__spinner-label">Loading your agreement...</p>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="sign-page sign-page--centered">
        <div className="sign-page__message-card sign-page__message-card--error">
          <div className="sign-page__icon sign-page__icon--error">✕</div>
          <h1>Link Unavailable</h1>
          <p>{loadError}</p>
          <p>
            If you believe this is a mistake, contact us at{' '}
            <a href="mailto:info@instant-meals.co.za">info@instant-meals.co.za</a>
          </p>
        </div>
      </div>
    );
  }

  if (done) {
    return (
      <div className="sign-page sign-page--centered">
        <div className="sign-page__message-card sign-page__message-card--success">
          <div className="sign-page__icon sign-page__icon--success">✓</div>
          <h1>Agreement Signed</h1>
          <p>
            Welcome to the team, <strong>{consultant?.firstName}</strong>! Your account is now active.
            We'll be in touch with your coupon code and everything you need to get started.
          </p>
        </div>
      </div>
    );
  }

  const nameValid = signedName.trim().length > 1;

  const stepState = (i) => {
    if (i < 2) return 'done';
    if (i === 2 && canSubmit) return 'active';
    if (i === 2 && nameValid) return 'active';
    return 'upcoming';
  };

  return (
    <div className="sign-page">

      <header className="sign-page__header">
        <div className="sign-page__brand">
          <div className="sign-page__brand-mark">IM</div>
          <span className="sign-page__brand-name">Instant Meals</span>
        </div>
        <span className="sign-page__header-badge">Consultant Agreement</span>
      </header>

      <main className="sign-page__body">

        <section className="sign-page__intro">
          <h1>Hi {consultant?.firstName},</h1>
          <p>
            Please read the Brand Consultant Agreement below, confirm your details are correct,
            then sign with your full legal name to activate your account.
          </p>
        </section>

        <div className="sign-page__steps">
          {STEPS.map((step, i) => (
            <div
              key={step.label}
              className={`sign-page__step sign-page__step--${stepState(i)}`}
            >
              <span className="sign-page__step-num">{i + 1}</span>
              <span className="sign-page__step-label">{step.label}</span>
            </div>
          ))}
        </div>

        <section className="sign-page__contract">
          <div className="sign-page__contract-header">
            <div className="sign-page__contract-title">
              <strong>Brand Consultant Agreement</strong>
              <span>Instant Meals (Pty) Ltd</span>
            </div>
            <span className="sign-page__contract-date">{CONTRACT_DATE}</span>
          </div>
          <div className="sign-page__contract-body">
            {CONTRACT_SECTIONS.map((s) => (
              <div key={s.heading} className="sign-page__contract-clause">
                <h3>{s.heading}</h3>
                <p>{s.body}</p>
              </div>
            ))}
          </div>
          <div className="sign-page__contract-footer">
            Scroll to read the full agreement before signing
          </div>
        </section>

        <section className="sign-page__details-card">
          <div className="sign-page__details-header">Your Details On File</div>
          <div className="sign-page__details-grid">
            <span>Name</span>
            <span>{consultant?.firstName} {consultant?.lastName}</span>
            <span>Email</span>
            <span>{consultant?.email}</span>
            {consultant?.socialHandle && (
              <>
                <span>Social</span>
                <span>{consultant.socialHandle} ({consultant.socialPlatform})</span>
              </>
            )}
            {consultant?.commissionRate && (
              <>
                <span>Commission</span>
                <span>{consultant.commissionRate}%</span>
              </>
            )}
          </div>
        </section>

        <section className="sign-page__bank-details">
          <div className="sign-page__details-header">Payment Details</div>
          <p className="sign-page__bank-intro">
            Please provide your bank account details so your commission payments can be processed.
            This information will be included in your signed agreement.
          </p>
          <div className="sign-page__bank-grid">
            <Input
              label="Bank Name" name="bankName" value={bankName}
              onChange={({ value }) => setBankName(value)} filled bordered
            />
            <Input
              label="Account Holder Name" name="bankAccountHolder" value={bankAccountHolder}
              onChange={({ value }) => setBankAccountHolder(value)} filled bordered
            />
            <Input
              label="Account Number" name="bankAccountNumber" value={bankAccountNumber}
              onChange={({ value }) => setBankAccountNumber(value)} filled bordered
            />
            <Input
              label="Branch Code" name="bankBranchCode" value={bankBranchCode}
              onChange={({ value }) => setBankBranchCode(value)} filled bordered
            />
          </div>
        </section>

        <section className="sign-page__signature">
          <div className="sign-page__sig-header">
            <h2>Electronic Signature</h2>
            <div className="sign-page__sig-tabs">
              <button
                type="button"
                className={`sign-page__sig-tab${sigMode === 'draw' ? ' sign-page__sig-tab--active' : ''}`}
                onClick={() => setSigMode('draw')}
              >
                Draw
              </button>
              <button
                type="button"
                className={`sign-page__sig-tab${sigMode === 'type' ? ' sign-page__sig-tab--active' : ''}`}
                onClick={() => setSigMode('type')}
              >
                Type
              </button>
            </div>
          </div>
          <div className="sign-page__sig-body">
            {sigMode === 'draw' ? (
              <>
                <p className="sign-page__sig-instruction">
                  Draw your signature below using your mouse or finger.
                </p>
                <SignaturePad onChange={setSignatureImage} />
              </>
            ) : (
              <>
                <p className="sign-page__sig-instruction">
                  Type your full legal name to confirm you have read and agree to the terms above.
                </p>
              </>
            )}

            <div className="sign-page__sig-input">
              <Input
                label="Full Legal Name"
                name="signedName"
                value={signedName}
                onChange={({ value }) => setSignedName(value)}
                placeholder={`${consultant?.firstName ?? ''} ${consultant?.lastName ?? ''}`}
                filled
                bordered
              />
            </div>

            <label className="sign-page__send-copy">
              <input
                type="checkbox"
                checked={sendCopy}
                onChange={(e) => setSendCopy(e.target.checked)}
              />
              <span>Send me a copy of the signed agreement</span>
            </label>

            <p className="sign-page__sig-note">
              By signing, you confirm you are <strong>{consultant?.firstName} {consultant?.lastName}</strong> and
              agree to be bound by these terms. Your signature, name, timestamp, and IP address will be recorded as proof of signing.
            </p>

            {submitError && (
              <div className="sign-page__error">{submitError}</div>
            )}

            <div className="sign-page__sig-actions">
              <Button
                onClick={handleSubmit}
                loading={submitting}
                disabled={!canSubmit}
              >
                Sign &amp; Activate My Account
              </Button>
            </div>
          </div>
        </section>

      </main>

      <footer className="sign-page__footer">
        <p>Instant Meals · Powered by Nextline</p>
      </footer>

    </div>
  );
};
